import {REST_METHODS} from '@/src/app/constants/api';
import {ROLES_NAMES} from '@/src/app/constants/roles';
import {
  ITEMS_PER_PAGE,
  RESULT_DATA_TYPE,
  SEARCH_FILTERS,
  SEARCH_TYPE,
} from '@/src/app/constants/search';
import authorizedFetch from '@/src/app/utils/authorizedFetch';
import unauthorizedFetch from '@/src/app/utils/unauthorizedFetch';

import {PATHS} from '../../routes/containers/Routes';

const getSearchDataPath = (slug) => {
  switch (slug) {
    case SEARCH_TYPE.USERS: {
      return `/api/v1/users/search`;
    }
    case SEARCH_TYPE.FOLLOWERS: {
      return '/api/v1/follows/followers';
    }
    case SEARCH_TYPE.FOLLOWING: {
      return '/api/v1/follows/followees';
    }
    case SEARCH_TYPE.WATCHLIST_BOOKMARKED: {
      return `/api/v1/courses/bookmarks`;
    }
    case SEARCH_TYPE.WATCHLIST_CONTINUE: {
      return '/api/v1/courses/watched_courses';
    }
    case SEARCH_TYPE.WATCHLIST_FINISHED: {
      return '/api/v1/courses/finished_courses';
    }
    case SEARCH_TYPE.POSTS: {
      return '/api/v1/topics/search';
    }
    case SEARCH_TYPE.COURSES: {
      return `/api/v1/courses/search`;
    }
    case SEARCH_TYPE.EMPLOYEES: {
      return '/api/v1/clinics/clinic_employees';
    }
    case SEARCH_TYPE.ALL_CANDIDATES: {
      return '/api/v1/clinics/candidates';
    }
    case SEARCH_TYPE.CLINIC_VACANCIES:
    case SEARCH_TYPE.VACANCIES: {
      return '/api/v1/job_portal/vacancies_search';
    }
    case SEARCH_TYPE.CLINICS: {
      return '/api/v1/job_portal/clinics_search';
    }
    case SEARCH_TYPE.ALL_POSITIONS: {
      return '/api/v1/job_portal/vacancies_search';
    }
  }
};

export const getResultDataType = (slug) => {
  switch (slug) {
    case SEARCH_TYPE.USERS:
    case SEARCH_TYPE.EMPLOYEES:
    case SEARCH_TYPE.ALL_CANDIDATES: {
      return RESULT_DATA_TYPE.USER;
    }

    case SEARCH_TYPE.FOLLOWERS:
    case SEARCH_TYPE.FOLLOWING: {
      return RESULT_DATA_TYPE.FOLLOW;
    }

    case SEARCH_TYPE.COURSES:
    case SEARCH_TYPE.WATCHLIST_BOOKMARKED:
    case SEARCH_TYPE.WATCHLIST_CONTINUE:
    case SEARCH_TYPE.WATCHLIST_FINISHED: {
      return RESULT_DATA_TYPE.COURSE;
    }
    case SEARCH_TYPE.CLINIC_VACANCIES:
    case SEARCH_TYPE.VACANCIES:
    case SEARCH_TYPE.ALL_POSITIONS: {
      return RESULT_DATA_TYPE.VACANCY;
    }

    case SEARCH_TYPE.CLINICS:
      return RESULT_DATA_TYPE.CLINICS;

    case SEARCH_TYPE.POSTS:
      return RESULT_DATA_TYPE.POSTS;

    default:
      break;
  }
};

export const getPathnameFromSearchType = (
  slug,
  targetUsername = '',
  userType
) => {
  const {
    SEARCH_COURSES,
    SEARCH_USERS,
    SEARCH_FOLLOWERS_ROUTE,
    SEARCH_FOLLOWINGS_ROUTE,
    CLINIC_SEARCH_FOLLOWERS_ROUTE,
    CLINIC_SEARCH_FOLLOWINGS_ROUTE,
    WATCHLIST_BOOKMARKED_ROUTE,
    WATCHLIST_CONTINUE_ROUTE,
    WATCHLIST_FINISHED_ROUTE,
    SEARCH_UNITED_ROUTE,
    SEARCH_EMPLOYEES_ROUTE,
    SEARCH_ALL_CANDIDATES_ROUTE,
    SEARCH_ALL_POSITIONS_ROUTE,
    SEARCH_CLINIC_VACANCIES,
    SEARCH_CLINICS,
    SEARCH_VACANCIES,
    SEARCH_POSTS,
  } = PATHS;

  switch (slug) {
    case SEARCH_TYPE.UNITED: {
      return SEARCH_UNITED_ROUTE.getLink();
    }
    case SEARCH_TYPE.COURSES: {
      return SEARCH_COURSES.getLink();
    }
    case SEARCH_TYPE.USERS: {
      return SEARCH_USERS.getLink();
    }
    case SEARCH_TYPE.CLINICS: {
      return SEARCH_CLINICS.getLink();
    }
    case SEARCH_TYPE.POSTS: {
      return SEARCH_POSTS.getLink();
    }
    case SEARCH_TYPE.FOLLOWERS: {
      return userType === ROLES_NAMES.CLINIC
        ? CLINIC_SEARCH_FOLLOWERS_ROUTE.getLink(targetUsername)
        : SEARCH_FOLLOWERS_ROUTE.getLink(targetUsername);
    }
    case SEARCH_TYPE.FOLLOWING: {
      return userType === ROLES_NAMES.CLINIC
        ? CLINIC_SEARCH_FOLLOWINGS_ROUTE.getLink(targetUsername)
        : SEARCH_FOLLOWINGS_ROUTE.getLink(targetUsername);
    }
    case SEARCH_TYPE.WATCHLIST_BOOKMARKED: {
      return WATCHLIST_BOOKMARKED_ROUTE.getLink();
    }
    case SEARCH_TYPE.WATCHLIST_CONTINUE: {
      return WATCHLIST_CONTINUE_ROUTE.getLink();
    }
    case SEARCH_TYPE.WATCHLIST_FINISHED: {
      return WATCHLIST_FINISHED_ROUTE.getLink();
    }
    case SEARCH_TYPE.EMPLOYEES: {
      return SEARCH_EMPLOYEES_ROUTE.getLink();
    }
    case SEARCH_TYPE.ALL_CANDIDATES: {
      return SEARCH_ALL_CANDIDATES_ROUTE.getLink();
    }
    case SEARCH_TYPE.CLINIC_VACANCIES: {
      return SEARCH_CLINIC_VACANCIES.getLink();
    }
    case SEARCH_TYPE.VACANCIES: {
      return SEARCH_VACANCIES.getLink();
    }
    case SEARCH_TYPE.ALL_POSITIONS: {
      return SEARCH_ALL_POSITIONS_ROUTE.getLink();
    }
  }
};

export const checkExsistingFilter = (filters) => {
  let appliedFilters = {};

  for (const prop in SEARCH_FILTERS) {
    if (Object.hasOwnProperty.call(filters, SEARCH_FILTERS[prop])) {
      appliedFilters = {
        ...appliedFilters,
        [SEARCH_FILTERS[prop]]: filters[SEARCH_FILTERS[prop]],
      };
    }
  }

  return appliedFilters;
};

export const serializeFilters = (filters = {}) =>
  Object.keys(filters)
    .filter((key) => filters[key]?.length > 0)
    .map((key) => key + '=' + filters[key])
    .join('&');

const getIsPublicFilterValue = (selected = []) => {
  const safeSelected = Array.isArray(selected) ? selected : [];
  const hasFree = safeSelected.includes('free');
  const hasSubscription = safeSelected.includes('subscription');

  if (hasFree && !hasSubscription) return true;
  if (!hasFree && hasSubscription) return false;
  return undefined;
};

export const getData = (
  params = {},
  slug,
  targetUserId,
  currentUserId,
  signal
) => {
  let arr = [];

  Object.entries(checkExsistingFilter(params)).forEach(([key, value]) => {
    key !== SEARCH_FILTERS.AVAILABILITY &&
      value?.toString().length > 0 &&
      arr.push({
        name: key,
        value: value,
      });
  });

  const availabilityRaw = params?.[SEARCH_FILTERS.AVAILABILITY];
  if (availabilityRaw?.toString().length > 0) {
    try {
      const selected = JSON.parse(availabilityRaw);
      const isPublic = getIsPublicFilterValue(selected);

      if (typeof isPublic === 'boolean') {
        arr.push({name: SEARCH_FILTERS.FILTERS_IS_PUBLIC, value: isPublic});
      }
    } catch (e) {
      // ignore invalid query value
    }
  }

  const param = [
    {name: SEARCH_FILTERS.PER_PAGE, value: ITEMS_PER_PAGE[slug]},
    ...arr,
    ...(targetUserId ? [{name: 'user_id', value: targetUserId}] : []),
    ...(currentUserId ? [{name: 'current_user_id', value: currentUserId}] : []),
  ];

  // @ts-ignore
  return authorizedFetch({
    method: REST_METHODS.GET,
    // @ts-ignore
    path: getSearchDataPath(params.slug),
    queryParams: param,
    signal,
    shouldHandleNotFound: false,
    shouldSkipUnauthorizedStatus: true,
  });
};

export const getCategories = () => {
  return unauthorizedFetch({
    method: REST_METHODS.GET,
    path: `/api/v1/categories`,
    // @ts-ignore
    shouldHandleNotFound: false,
    shouldSkipUnauthorizedStatus: true,
  });
};
